import { Mob } from './Mob';

export class MeleeMob extends Mob {
    constructor(scene, x, y, config = {}) {
        // Set default melee-specific configurations
        const meleeConfig = {
            enemyType: 'melee',
            health: config.health || 80,
            speed: config.speed || 60,
            damage: config.damage || 15,
            attackRange: config.attackRange || 2,
            detectionRange: config.detectionRange || 120,
            attackCooldown: config.attackCooldown || 1500,
            shouldPatrol: config.shouldPatrol !== false, // Default true for melee
            ...config
        };

        super(scene, x, y, meleeConfig);
        
        // Melee-specific properties
        this.chargeSpeed = config.chargeSpeed || this.speed * 1.5;
        this.chargeRange = config.chargeRange || 80;
        this.isCharging = false;
        this.chargeTimer = 0;
        this.chargeDuration = 1000; // 1 second charge
    }

    setupStateMachine() {
        super.setupStateMachine();
        
        // Add melee-specific states if needed
        // Could add ChargingState for more aggressive melee behavior
    }

    moveTowardsPlayer() {
        const player = this.getPlayer();
        if (!player) return;

        const distance = this.getDistanceToPlayer();
        const direction = this.getDirectionToPlayer();
        this.facingDirection = direction.x > 0 ? 1 : -1;

        // Only zombies and bomb zombies should charge (not all melee mobs)
        if (distance <= this.chargeRange && distance > this.attackRange && !this.isCharging && 
            (this.mobType === 'zombie' || this.mobType === 'bomb_zombie')) {
            this.startCharge();
        }

        // Use charge speed if charging, normal speed otherwise
        const moveSpeed = this.isCharging ? this.chargeSpeed : this.speed;
        this.setVelocity(direction.x * moveSpeed, direction.y * moveSpeed);
    }

    startCharge() {
        this.isCharging = true;
        this.chargeTimer = this.chargeDuration;
        
        // Visual effect for charging (could add particle effects)
        //this.sprite.setTint(0xff6666); // Red tint to indicate charging
    }

    update(deltaTime) {
        super.update(deltaTime);

        // Handle charging behavior
        if (this.isCharging) {
            this.chargeTimer -= deltaTime;
            if (this.chargeTimer <= 0) {
                this.stopCharge();
            }
        }
    }

    stopCharge() {
        this.isCharging = false;
        this.chargeTimer = 0;
        this.sprite.clearTint(); // Remove red tint
    }

    takeDamage(amount, damageDirection = null) {
        // Stop charging when taking damage
        if (this.isCharging) {
            this.stopCharge();
        }
        
        return super.takeDamage(amount, damageDirection);
    }

    // Melee mobs deal more damage and have knockback
    dealDamageToPlayer() {
        const player = this.getPlayer();
        if (player && player.modifyHealth) {
            let damage = this.damage;
            
            // Extra damage if charging
            if (this.isCharging) {
                damage *= 1.5;
            }
            
            player.modifyHealth(-Math.round(damage)); // Use negative value to reduce health
        }
    }

    onDeath() {
        super.onDeath();
        
        // Melee mobs might drop melee-specific loot
        this.scene.events.emit('mobDropLoot', {
            mobType: this.mobType,
            enemyType: this.enemyType,
            position: { x: this.x, y: this.y },
            lootType: 'melee'
        });
    }
}

// Specific melee mob types
export class ZombieMob extends MeleeMob {
    constructor(scene, x, y, config = {}) {
        super(scene, x, y, {
            mobType: 'zombie',
            health: 60,
            speed: 45,
            damage: 12,
            // Audio configuration
            attackSounds: ['zombie-attack-1', 'zombie-attack-2'],
            damageSounds: ['zombie-hurt-1', 'zombie-hurt-2'],
            deathSounds: ['zombie-death-1', 'zombie-death-2'],
            ...config
        });
    }
}

export class DummyMob extends MeleeMob {
    constructor(scene, x, y, config = {}) {
        super(scene, x, y, {
            mobType: 'dummy',
            health: 40,
            speed: 0, // Dummies don't move
            damage: 8,
            shouldPatrol: false,
            attackRange: 15, // Larger attack range since they can't move
            // Audio configuration (minimal sounds for dummy)
            attackSounds: ['sword-swing-1'],
            damageSounds: ['zombie-hurt-1'],
            deathSounds: ['zombie-death-1'],
            ...config
        });
    }

    // Override movement methods to keep dummy stationary
    moveTowardsPlayer() {
        // Dummies don't move, they just attack if player is in range
        if (this.isPlayerInAttackRange()) {
            this.stateMachine.changeState('AttackState');
        } else {
            this.stateMachine.changeState('IdleState');
        }
    }

    startCharge() {
        // Dummies don't charge
    }

    stopCharge() {
        // Dummies don't charge, so no tint to clear
    }
}

export class BombZombieMob extends MeleeMob {
    constructor(scene, x, y, config = {}) {
        super(scene, x, y, {
            mobType: 'bomb_zombie',
            health: 50,
            speed: 70, // Faster but less health
            damage: 25, // High damage but one-shot
            attackRange: 30,
            // Audio configuration
            attackSounds: ['zombie-attack-3', 'zombie-attack-4'],
            damageSounds: ['zombie-hurt-1', 'zombie-hurt-2'],
            deathSounds: ['bomb-zombie-death'], 
            ...config
        });

        this.isExploding = false;
        this.explosionRadius = 60;
    }

    dealDamageToPlayer() {
        if (!this.isExploding) {
            this.explode();
        }
    }

    explode() {
        this.isExploding = true;
        
        // Deal damage in explosion radius
        const player = this.getPlayer();
        if (player && this.getDistanceToPlayer() <= this.explosionRadius) {
            player.modifyHealth(-this.damage); // Use negative value to reduce health
        }

        // Visual explosion effect (could add particles here)
        this.scene.add.circle(this.x, this.y, this.explosionRadius, 0xff4444, 0.3)
            .setDepth(15)
            .setScale(0.1)
            .setAlpha(0.8);

        // Animate explosion
        this.scene.tweens.add({
            targets: this.scene.children.getByName('explosion') || this.scene.children.list[this.scene.children.list.length - 1],
            scaleX: 1,
            scaleY: 1,
            alpha: 0,
            duration: 500,
            ease: 'Power2',
            onComplete: () => {
                // Explosion finished
            }
        });

        // Kill the bomb zombie
        this.currentHealth = 0;
        this.stateMachine.changeState('DeathState');
    }
}

export class JumpZombieMob extends MeleeMob {
    constructor(scene, x, y, config = {}) {
        
        try {
            super(scene, x, y, {
                mobType: 'jump_zombie',
                health: 70,
                speed: 40,
                damage: 18,
                attackRange: 80, // Larger attack range for lunge attacks
                detectionRange: 250, // Long detection range for jump attacks
                // Audio configuration
                attackSounds: ['zombie-attack-1'],
                damageSounds: ['zombie-hurt-1', 'zombie-hurt-2'],
                deathSounds: ['zombie-death-1', 'zombie-death-2'],
                ...config
            });

            this.jumpCooldown = 2000; // 2 seconds between jumps - more aggressive
            this.lastJumpTime = 0;
            
        } catch (error) {
            console.error(`JumpZombieMob constructor failed:`, error);
            throw error;
        }
    }

    moveTowardsPlayer() {
        const distance = this.getDistanceToPlayer();
        const currentTime = this.scene.time.now;

        // Jump zombies prefer jumping over walking - jump from long range!
        if (distance >= 60 && // Don't jump if too close
            distance <= this.detectionRange && 
            currentTime - this.lastJumpTime >= this.jumpCooldown) {
            
            this.lastJumpTime = currentTime;
            this.stateMachine.changeState('JumpState');
            return;
        }

        // Only walk if jump is on cooldown and we're not too close
        if (distance > 30) {
            const direction = this.getDirectionToPlayer();
            this.facingDirection = direction.x > 0 ? 1 : -1;
            this.setVelocity(direction.x * this.speed * 0.7, direction.y * this.speed * 0.7); // Slower walk, prefer jumping
        } else {
            // Stop if too close and jump on cooldown
            this.setVelocity(0, 0);
        }
    }

    // Jump zombies don't charge - they jump instead
    startCharge() {
        // No charging for jump zombies
    }

    stopCharge() {
        // No charging, so no tint to clear
    }
} 